/**
 * SearchInput — a text input with a leading search icon and a trailing clear (✕)
 * button that appears once there's text (Escape also clears). Gives every filter
 * bar the same search UX. Modeled on the bank-overview search box.
 * For Media Devoted | Created By James Alexandrov
 */

import { FC, InputHTMLAttributes } from "react";
import { Search, X } from "lucide-react";

import { cn } from "@/lib/utils";

import { Input } from "./Input";

interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  value: string;
  onValueChange: (value: string) => void;
  /** Wrapper className — the box grows to fill its row by default. */
  containerClassName?: string;
}

export const SearchInput: FC<SearchInputProps> = ({
  value,
  onValueChange,
  containerClassName,
  className,
  ...rest
}) => (
  <div className={cn("relative flex-1", containerClassName)}>
    <Search
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      aria-hidden
    />
    <Input
      type="search"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") onValueChange("");
      }}
      className={cn(
        "pl-10 pr-9 [&::-webkit-search-cancel-button]:hidden",
        className,
      )}
      {...rest}
    />
    {value && (
      <button
        type="button"
        onClick={() => onValueChange("")}
        aria-label="Clear search"
        title="Clear"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

export default SearchInput;
